import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, formatDate, maskMoneyInput, parseInputToApi } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { Contract, ContractDetail } from './types';

/**
 * Ficha do contrato — histórico e ações.
 *
 * As três ações que só existem aqui — reajustar, renovar e encerrar — mudam o
 * comportamento futuro sem reescrever o passado. Cada uma diz na tela o que
 * acontece com os títulos já gerados, porque é a primeira dúvida de quem clica.
 */
export function ContractPanel({
  contractId,
  selectedIds,
  onChanged,
}: {
  contractId: number;
  selectedIds: number[];
  onChanged: () => void;
}) {
  const [action, setAction] = useState<'adjust' | 'renew' | 'terminate' | null>(null);

  const detail = useQuery({
    queryKey: ['contract', contractId, selectedIds],
    queryFn: () =>
      api.get<{ data: ContractDetail }>(`/contracts/${contractId}`, { entityIds: selectedIds }),
    select: (r) => r.data,
  });

  if (detail.isPending) {
    return (
      <div className="mt-3 animate-pulse rounded-xl border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
        <div className="h-24 rounded bg-slate-100 dark:bg-slate-800" />
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300">
        Não foi possível carregar o contrato.
      </p>
    );
  }

  const c = detail.data;
  const encerrado = ['encerrado', 'cancelado'].includes(c.status);

  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 p-3.5 dark:border-slate-800">
        <h2 className="font-medium">{c.title}</h2>

        {!encerrado && (
          <div className="ml-auto flex flex-wrap gap-2 text-xs">
            <button
              onClick={() => setAction('adjust')}
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
            >
              Reajustar
            </button>
            {c.ends_at && (
              <button
                onClick={() => setAction('renew')}
                className="rounded-lg border border-slate-300 px-2.5 py-1.5 transition hover:border-indigo-500 hover:text-indigo-600 dark:border-slate-700"
              >
                Renovar
              </button>
            )}
            <button
              onClick={() => setAction('terminate')}
              className="rounded-lg border border-slate-300 px-2.5 py-1.5 transition hover:border-rose-500 hover:text-rose-600 dark:border-slate-700"
            >
              Encerrar
            </button>
          </div>
        )}
      </div>

      <div className="grid gap-4 p-3.5 sm:grid-cols-2 lg:grid-cols-3">
        <Info label="Valor atual" value={formatMoney(c.amount)} />
        <Info
          label="Vigência"
          value={c.ends_at ? `${formatDate(c.starts_at)} a ${formatDate(c.ends_at)}` : 'Indeterminada'}
        />
        <Info
          label="Renovação"
          value={
            c.auto_renew
              ? `Automática · ${c.renewal_months} meses · avisar ${c.notice_days} dias antes`
              : 'Manual'
          }
        />
        <Info
          label="Reajuste"
          value={
            c.adjustment_index === 'nenhum' || c.adjustment_index === null
              ? 'Sem reajuste'
              : `${c.adjustment_index}${c.adjustment_percentage ? ` · ${c.adjustment_percentage}%` : ''}${
                  c.next_adjustment_at ? ` · próximo em ${formatDate(c.next_adjustment_at)}` : ''
                }`
          }
        />
        <Info label="Próximo faturamento" value={c.next_billing_at ? formatDate(c.next_billing_at) : '—'} />
        <Info
          label="Total contratado"
          value={c.total_committed ? formatMoney(c.total_committed) : 'indeterminado'}
        />
      </div>

      {c.parties.length > 0 && (
        <div className="border-t border-slate-200 px-3.5 py-3 dark:border-slate-800">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Partes</p>
          <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-1 text-sm">
            {c.parties.map((p) => (
              <span key={p.id}>
                <span className="text-slate-500 dark:text-slate-400">{p.role}: </span>
                {p.party_name}
                {p.is_primary && <span className="ml-1 text-[10px] uppercase text-indigo-500">principal</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {c.adjustments.length > 0 && (
        <div className="border-t border-slate-200 px-3.5 py-3 dark:border-slate-800">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Histórico de reajustes
          </p>
          <div className="mt-1.5 space-y-1 text-sm">
            {c.adjustments.map((a, i) => (
              <p key={i}>
                {formatDate(a.applied_at)} · {formatMoney(a.from)} → <strong>{formatMoney(a.to)}</strong>
                {a.percentage && ` (${a.percentage}%`}
                {a.percentage && a.index && ` ${a.index}`}
                {a.percentage && ')'}
                {a.note && <span className="ml-1 text-xs text-slate-400">{a.note}</span>}
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="border-t border-slate-200 px-3.5 py-3 dark:border-slate-800">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Títulos gerados
        </p>

        {c.transactions.length === 0 ? (
          <p className="mt-1.5 text-sm text-slate-500 dark:text-slate-400">
            Nenhum título gerado ainda.
          </p>
        ) : (
          <div className="mt-1.5 max-h-64 overflow-y-auto">
            {c.transactions.map((t) => (
              <div
                key={t.id}
                className="flex items-center gap-3 border-b border-slate-100 py-1.5 text-sm last:border-0 dark:border-slate-800"
              >
                <span className="w-20 shrink-0 text-slate-500 dark:text-slate-400">
                  {formatDate(t.due_date)}
                </span>
                <span className="min-w-0 flex-1 truncate">{t.description}</span>
                <span className="shrink-0 font-medium">{formatMoney(t.amount)}</span>
                <span className="w-24 shrink-0 text-right text-xs text-slate-400">{t.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {action && (
        <ActionDialog
          kind={action}
          contract={c}
          selectedIds={selectedIds}
          onClose={() => setAction(null)}
          onDone={() => {
            setAction(null);
            void detail.refetch();
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm">{value}</p>
    </div>
  );
}

// -------------------------------------------------------------------

/**
 * Reajustar, renovar e encerrar.
 *
 * Exportado porque a LISTA também precisa de "Encerrar": deixá-lo só aqui
 * dentro obrigava o usuário a descobrir que a linha expande antes de conseguir
 * encerrar um contrato — e ninguém descobre isso procurando por "excluir".
 */
export function ActionDialog({
  kind,
  contract,
  selectedIds,
  onClose,
  onDone,
}: {
  kind: 'adjust' | 'renew' | 'terminate';
  contract: Contract;
  selectedIds: number[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [percentage, setPercentage] = useState('');
  const [newAmount, setNewAmount] = useState('');
  const [note, setNote] = useState('');
  const [months, setMonths] = useState(String(contract.renewal_months ?? 12));
  const [endedAt, setEndedAt] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: () => {
      const opts = { entityIds: selectedIds };

      if (kind === 'adjust') {
        return api.post(
          `/contracts/${contract.id}/adjust`,
          {
            percentage: percentage === '' ? null : Number(percentage.replace(',', '.')),
            new_amount: newAmount === '' ? null : parseInputToApi(newAmount),
            note: note || null,
          },
          opts,
        );
      }

      if (kind === 'renew') {
        return api.post(`/contracts/${contract.id}/renew`, { months: Number(months) }, opts);
      }

      return api.post(
        `/contracts/${contract.id}/terminate`,
        { ended_at: endedAt, reason: reason || null },
        opts,
      );
    },
    onSuccess: (res: unknown) => {
      const note = (res as { meta?: { note?: string } })?.meta?.note;
      if (note) alert(note);
      onDone();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Não foi possível concluir.'),
  });

  const titles = {
    adjust: 'Aplicar reajuste',
    renew: 'Renovar contrato',
    terminate: 'Encerrar contrato',
  };

  return (
    <Modal title={titles[kind]} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          run.mutate();
        }}
        className="space-y-3.5"
      >
        {kind === 'adjust' && (
          <>
            {/* A frase que evita a dúvida mais comum ao clicar. */}
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
              Vale para os <strong>próximos</strong> títulos. Os já gerados mantêm o valor antigo —
              o que foi cobrado por {formatMoney(contract.amount)} continua valendo isso.
              {contract.adjustment_index !== 'fixo' && contract.adjustment_index !== 'nenhum' && (
                <>
                  {' '}
                  O sistema não estima {contract.adjustment_index}: consulte o acumulado na fonte
                  oficial e informe abaixo.
                </>
              )}
            </p>

            <Field label="Percentual aplicado" hint="Ex.: 4,62 para 4,62%">
              <input
                value={percentage}
                onChange={(e) => {
                  setPercentage(e.target.value);
                  if (e.target.value !== '') setNewAmount('');
                }}
                inputMode="decimal"
                placeholder={contract.adjustment_percentage?.toString() ?? '0,00'}
                className="input w-full"
              />
            </Field>

            <p className="text-center text-xs text-slate-400">ou</p>

            <Field label="Novo valor direto" hint="Use quando o valor foi negociado, não calculado.">
              <input
                value={newAmount}
                onChange={(e) => {
                  setNewAmount(maskMoneyInput(e.target.value));
                  if (e.target.value !== '') setPercentage('');
                }}
                inputMode="decimal"
                placeholder="R$ 0,00"
                className="input w-full"
              />
            </Field>

            <Field label="Observação" hint="Ex.: IPCA acumulado 12 meses até jul/2026">
              <input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={500}
                className="input w-full"
              />
            </Field>
          </>
        )}

        {kind === 'renew' && (
          <>
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:bg-slate-800/50 dark:text-slate-400">
              A nova vigência conta a partir de <strong>{formatDate(contract.ends_at!)}</strong>, o
              fim atual — e não de hoje.
            </p>

            <Field label="Renovar por quantos meses" required>
              <input
                type="number"
                value={months}
                onChange={(e) => setMonths(e.target.value)}
                min={1}
                max={600}
                required
                className="input w-full"
              />
            </Field>
          </>
        )}

        {kind === 'terminate' && (
          <>
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
              Os títulos com vencimento <strong>depois</strong> da data e ainda não pagos serão
              cancelados. O que já venceu continua devido — encerrar o contrato não perdoa a dívida
              que ele gerou.
            </p>

            <Field label="Encerrar em" required>
              <input
                type="date"
                value={endedAt}
                onChange={(e) => setEndedAt(e.target.value)}
                required
                className="input w-full"
              />
            </Field>

            <Field label="Motivo">
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="Rescisão amigável"
                className="input w-full"
              />
            </Field>
          </>
        )}

        {error && (
          <p className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950/30 dark:text-rose-300">
            {error}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={run.isPending}
            className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition disabled:opacity-50 ${
              kind === 'terminate' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            {run.isPending ? 'Salvando…' : titles[kind]}
          </button>
        </div>
      </form>
    </Modal>
  );
}
